import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { useChangeAwareEffect } from 'use-change-aware-effect';
import { TZ } from '../../../../../shared/consts';
import { selectorProps } from '../styles';

const Container = styled.div`
  max-width: ${props => (props.theme.isXLarge ? '330px' : 'none')};
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${props => !props.yearOnly && `
    &:after {
      content: "-";
      order: 0;
      color: #d7dfe7;
      text-align: center;
      width: 16px;
      flex-grow: 0;
      flex-shrink: 0;
    }
  `};

  & div:not([class$="-menu"]) {
    width: unset !important;
    margin-left: unset !important;
  }

  & > .smonth-container {
    order: -1;
    flex-grow: 4;

  }

  & > .syr-container {
    order: 1;
    flex-grow: 3;
  }
`;

const thisYear = moment.tz(TZ).year();
const thisMonth = moment.tz(TZ).month();

const dumbMonth = { label: null, value: 0 };
const emptyMonth = { label: 'None', value: null };
const emptyYear = { label: 'None', value: null };

export default function MonthYearSelector({
  value, onDateSelected, yearOnly, min, max, hasEmptyOption,
}) {
  const [month, setMonth] = React.useState(null);
  const [year, setYear] = React.useState(null);
  const [changed, setChanged] = React.useState(false);
  const bound = {
    min: min ? moment(min, 'MM-YYYY').tz(TZ) : null,
    max: max ? moment(max, 'MM-YYYY').tz(TZ) : null,
  };

  const validMonths = () => {
    const cy = _.get(year, 'value', -1);
    const monthOptions = _.times(12);
    let end = (bound.max && bound.max.year() === cy) ? (bound.max.month() + 1) : 12;
    if (cy === thisYear) {
      end = Math.min(end, thisMonth + 1);
    }
    const start = (bound.min && bound.min.year() === cy) ? bound.min.month() : 0;

    return monthOptions.slice(start, end).map(monthValue => ({
      label: moment({ month: monthValue }).format('MMMM'),
      value: monthValue,
    }));
  };

  const validYears = () => {
    const cm = _.get(month, 'value', -1);
    const yearOptions = _.times(60)
      .map(i => thisYear - i)
      .filter(y => {
        let lower = true;
        if (bound.min) {
          if (cm === -1 || cm > bound.min.month()) {
            lower = y >= bound.min.year();
          } else {
            lower = y > bound.min.year();
          }
        }
        let upper = true;
        if (bound.max) {
          if (cm < bound.max.month()) {
            upper = y <= bound.max.year();
          } else {
            upper = y < bound.max.year();
          }
        }
        if (cm > thisMonth) {
          upper = upper && (y < thisYear);
        }
        return lower && upper;
      });

    return yearOptions.map(y => ({ value: y, label: `${y}` }));
  };

  useChangeAwareEffect(({ isMount }) => {
    if (!isMount) {
      return;
    }
    yearOnly && setMonth(dumbMonth);
    if (value && value.length) {
      const m = moment(value).tz(TZ);
      yearOnly || setMonth({ label: m.format('MMMM'), value: m.month() });
      setYear({ label: m.year(), value: m.year() });
    }
  }, {});

  useChangeAwareEffect(({ did }) => {
    if (did.month.notChange && did.year.notChange) {
      return;
    }
    const m = _.get(month, 'value', -1);
    const y = _.get(year, 'value', -1);
    if (m !== null && m >= 0 && y !== null && y >= 0 && changed) {
      onDateSelected(moment.tz({ year: y, month: m }, TZ));
    }

    if (m === null && y === null) {
      onDateSelected(null);
    }
  }, { month, year, changed });

  const change = setter => v => {
    setChanged(true);
    setter(v);
  };

  const monthOptions = hasEmptyOption ? _.concat([emptyMonth], ...validMonths()) : validMonths();
  const yearOptions = hasEmptyOption ? _.concat([emptyYear], ...validYears()) : validYears();

  return (
    <Container yearOnly={yearOnly}>
      {!yearOnly && (
        <Select
          {...selectorProps}
          value={month}
          options={monthOptions}
          className="smonth-container"
          placeholder="Month"
          onChange={change(setMonth)}
        />
      )}
      <Select
        {...selectorProps}
        value={year}
        options={yearOptions}
        className="syr-container"
        placeholder="Year"
        onChange={change(setYear)}
      />
    </Container>
  );
}

MonthYearSelector.propTypes = {
  value: PropTypes.string,
  onDateSelected: PropTypes.func,
  yearOnly: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  hasEmptyOption: PropTypes.bool,
};
MonthYearSelector.defaultProps = {
  value: null,
  onDateSelected: () => null,
  yearOnly: false,
  min: null,
  max: null,
  hasEmptyOption: false,
};
